import { useCallback, useRef } from 'react';

/**
 * Desktop mouse users have no touch/trackpad gesture for horizontal-scroll rows (overflow-x-auto
 * chip lists etc.) — only click+drag. This wires that up, and swallows the click that would
 * otherwise fire on a chip/button under the cursor once a real drag has happened.
 */
export function useDragScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const dragging = useRef(false);
  const moved = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    dragging.current = true;
    moved.current = false;
    startX.current = e.pageX;
    startScroll.current = el.scrollLeft;
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const el = ref.current;
    if (!el || !dragging.current) return;
    const delta = e.pageX - startX.current;
    if (Math.abs(delta) > 4) moved.current = true;
    el.scrollLeft = startScroll.current - delta;
  }, []);

  const endDrag = useCallback(() => {
    dragging.current = false;
  }, []);

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (moved.current) {
      e.stopPropagation();
      e.preventDefault();
      moved.current = false;
    }
  }, []);

  return {
    ref,
    onMouseDown,
    onMouseMove,
    onMouseUp: endDrag,
    onMouseLeave: endDrag,
    onClickCapture,
    className: 'cursor-grab active:cursor-grabbing',
  };
}
